<template>
  <component
    :is="tag"
    class="app-button btn"
    :class="[
      { active: active },
      { 'btn-sm': size === 'sm' },
      { 'btn-lg': size === 'lg' },
      `btn-${variant}`,
    ]"
    :disabled="disabled"
    :href="tag === 'a' ? link : undefined"
    :data-bs-theme="theme"
    @click="onClick"
  >
    <component v-if="icon" :is="icon" class="app-button-icon" />

    <slot>{{ label }}</slot>

    <UISpinner v-show="loading" />
  </component>
</template>

<script lang="ts">
import { ComponentPublicInstance, computed, FunctionalComponent } from "vue";
import { useTheme } from "../themes/useTheme";

export type Tag = "button" | "a" | string | ComponentPublicInstance;
export type Size = "sm" | "md" | "lg";
export type Variant =
  | "primary"
  | "outline-primary"
  | "secondary"
  | "outline-secondary"
  | "success"
  | "outline-success"
  | "danger"
  | "outline-danger"
  | "warning"
  | "outline-warning"
  | "info"
  | "outline-info"
  | "light"
  | "dark"
  | "link";

export type UIButtonProps = {
  active?: boolean;
  disabled?: boolean;
  external?: boolean;
  label?: string;
  /** Link to an URL. */
  link?: string;
  loading?: boolean;
  icon?: ComponentPublicInstance | FunctionalComponent;
  /** Link to another page. */
  page?: string;
  size?: Size;
  tag?: Tag;
  variant?: Variant;
};

export type UIButtonEvents = {
  (type: "click", event: Event): unknown;
};

export default {
  inheritAttrs: true,
};
</script>

<script setup lang="ts">
import UISpinner from "@/components/UI/UISpinner.vue";

const props = withDefaults(defineProps<UIButtonProps>(), {
  tag: "button",
  variant: "primary",
});
const emit = defineEmits<UIButtonEvents>();
const theme = useTheme();

const link = computed<string | undefined>(() => props.page ?? props.link);

function onClick(event: Event) {
  if (link.value && props.tag !== "a") {
    if (props.external) {
      window.open(link.value, "_blank");
    } else {
      window.location.href = link.value;
    }
  }
  emit("click", event);
}
</script>

<style lang="scss">
.btn-secondary {
  color: var(--white);
}

.btn-link {
  color: var(--dark);
  text-decoration: underline;
}

.app-button {
  &-icon {
    height: 30px;
    margin-right: 4px;

    .btn-sm & {
      height: 20px;
    }

    .btn-lg & {
      height: 40px;
    }
  }
}
</style>
